// İSG uzmanı portalı — belge türü seçici alanı (GL #7; tasarım review §4.1, plan Review Focus 2).
// Üç ekleme modalı (firma, personel, araç) ve onay-routing formu aynı seçici durumlarını paylaşır:
//   - yükleniyor: devre dışı `Select` + "Yükleniyor…" yer tutucusu (Spinner değil; yükseklik kaymaz);
//   - hata: yalnız `isError && !types` iken `Alert` + "Tekrar dene" — önbellek varken başarısız bir
//     tazeleme SESSİZ kalır;
//   - boş: seçilebilir aktif tür yoksa devre dışı `Select` + açıklama;
//   - hazır: "Belge türü seçin" + yalnız aktif türler (`selectable`), varsayılan tür YOK.
// Tek entity'li çağıran `entity` verir (değer = `code`); routing iki entity'yi birlikte sunduğu için
// seçenekleri kendisi hazırlar (`options`, değer = `entity:code`).
// Bu alan modal gövdesindedir ve yalnız modal açıkken mount edilir: `useRefetchDocumentTypesOnMount`
// kaydı her yükleme akışına girişte bir kez tazeler (D9, §27.7(2)).

import {
  Alert,
  Button,
  type DocumentEntity,
  Field,
  Select,
  type SelectOption,
  fieldDescribedBy,
  useDocumentTypes,
  useRefetchDocumentTypesOnMount,
} from '@isgportal/shared';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type DocumentTypeFieldProps = {
  id: string;
  label: string;
  /** Çözülmüş alan hatası (zorunlu seçim ya da sunucu reddi). */
  error?: string;
  /** Seçenek kalmadığında gösterilen açıklama; verilmezse `documentTypes.empty`. */
  emptyHint?: string;
  registration: UseFormRegisterReturn;
} & (
  | { entity: DocumentEntity; options?: undefined }
  /** Çağıranın kayıttan hazırladığı seçenekler (kayıt yüklenmemişse boş dizi). */
  | { entity?: undefined; options: SelectOption[] }
);

export function DocumentTypeField(props: DocumentTypeFieldProps) {
  const { id, label, error, emptyHint, registration } = props;
  const { t } = useTranslation();
  useRefetchDocumentTypesOnMount();
  const registry = useDocumentTypes();

  const loading = registry.isPending;
  const failed = registry.isError && registry.types === undefined;
  const options =
    props.entity === undefined
      ? props.options
      : registry
          .selectable(props.entity)
          .map((type) => ({ value: type.code, label: registry.label(props.entity, type.code) }));
  const empty = registry.types !== undefined && options.length === 0;
  const hint = empty ? (emptyHint ?? t('documentTypes.empty')) : undefined;

  return (
    <div className="flex flex-col gap-2">
      <Field htmlFor={id} label={label} error={error} hint={hint} required>
        <Select
          id={id}
          options={options}
          placeholder={loading ? t('documentTypes.loading') : t('documentTypes.placeholder')}
          disabled={loading || failed || empty}
          aria-busy={loading || undefined}
          aria-required="true"
          invalid={Boolean(error)}
          aria-describedby={fieldDescribedBy(id, error, hint !== undefined)}
          {...registration}
        />
      </Field>
      {failed ? (
        <Alert tone="danger">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>{t('documentTypes.loadError')}</span>
            <Button variant="ghost" size="sm" onClick={() => void registry.refetch()}>
              {t('documentTypes.retry')}
            </Button>
          </div>
        </Alert>
      ) : null}
    </div>
  );
}
